/**
 * The way into a machine's upgrades, on its Values page: a row with a pip per level (filled up to
 * its own, a dashed one for the level coming soon) and what is next: a level to upgrade to, one
 * under way, or one coming soon.
 */
import type { JSX } from 'react';

import { upgradeTrack } from '@game/upgrade/upgrades';
import { duration, t } from '@i18n/index';
import { useGame } from '@ui/hooks';
import { Icon } from '@ui/icons';

import './entry.css';

export function UpgradesEntry({
  tile,
  name,
  onOpen,
}: {
  tile: number;
  name: string;
  onOpen: () => void;
}): JSX.Element | null {
  const game = useGame();
  const track = upgradeTrack(game, tile);
  if (!track) return null;
  const { upgrade, comingSoon } = track;
  const next =
    upgrade?.kind === 'underway'
      ? `${t('upgrade.underway', { n: upgrade.to })} · ${t('upgrade.left', { time: duration(upgrade.left) })}`
      : upgrade
        ? t('upgrade.available', { n: upgrade.to })
        : comingSoon !== null
          ? t('upgrade.next-soon', { n: comingSoon })
          : t('upgrade.top');
  const ready = upgrade?.kind === 'offer' && upgrade.check.state === 'ok';
  return (
    <button
      type="button"
      className={`upgrades-entry${ready ? ' ready' : ''}`}
      data-open="upgrades"
      aria-label={`${t('upgrade.open-aria', { name: name.toLowerCase() })}: ${next}`}
      onClick={onOpen}
    >
      <span className="disc">
        <Icon id="up" />
      </span>
      <span className="words">
        <b>{t('upgrade.title')}</b>
        <small>{next}</small>
      </span>
      <span className="pips" aria-hidden="true">
        {track.levels.map((level) => (
          <i key={level.level} className={level.state} />
        ))}
        {comingSoon !== null && <i className="soon" />}
      </span>
      <Icon id="chev" />
    </button>
  );
}
